import type { PnidInstrument, PnidWizardState } from '../types';
import { hasManualInstrumentTag } from './instrumentIdentity';

export function isTemplateMatchInstrument(inst: PnidInstrument): boolean {
  return inst.provenance?.source === 'template_match';
}

export function isEquipmentContextInstrument(inst: PnidInstrument): boolean {
  return inst.context_type === 'equipment' && Boolean(inst.context_id);
}

export function isNodeContextInstrument(inst: PnidInstrument): boolean {
  return inst.context_type === 'node' && Boolean(inst.context_id);
}

export function isDerivedUntaggedInstrument(inst: PnidInstrument): boolean {
  return Boolean(inst.generated_id && !hasManualInstrumentTag(inst));
}

export type InstrumentEffectiveContext = { context_type: 'equipment' | 'node' | null; context_id: string | null };

export function getInstrumentEffectiveContext(
  inst: PnidInstrument,
  state: Pick<PnidWizardState, 'instruments'> | null | undefined,
): InstrumentEffectiveContext {
  if (inst.context_type === 'equipment' || inst.context_type === 'node') {
    return { context_type: inst.context_type, context_id: inst.context_id ?? null };
  }

  if (inst.context_type !== 'instrument' || !inst.context_id) {
    return { context_type: null, context_id: null };
  }

  const instrumentsById = new Map((state?.instruments ?? []).map((entry) => [entry.id, entry]));
  const seen = new Set<string>([inst.id]);
  let current: PnidInstrument | undefined = instrumentsById.get(inst.context_id);

  while (current) {
    if (seen.has(current.id)) {
      return { context_type: null, context_id: null };
    }
    seen.add(current.id);

    if (current.context_type === 'equipment' || current.context_type === 'node') {
      return { context_type: current.context_type, context_id: current.context_id ?? null };
    }

    if (current.context_type !== 'instrument' || !current.context_id) {
      return { context_type: null, context_id: null };
    }

    current = instrumentsById.get(current.context_id);
  }

  return { context_type: null, context_id: null };
}

export function getEquipmentContextInstruments(
  state: Pick<PnidWizardState, 'instruments'> | null | undefined,
  equipmentId?: string | null,
): PnidInstrument[] {
  const instruments = state?.instruments ?? [];
  return instruments.filter((inst) => {
    const effectiveContext = getInstrumentEffectiveContext(inst, state);
    if (effectiveContext.context_type !== 'equipment') return false;
    return equipmentId ? effectiveContext.context_id === equipmentId : true;
  });
}

/**
 * The instruments that hang off nothing: no equipment, no line, and no chain of
 * instrument contexts that reaches one. A P&ID with no equipment and no lines can only
 * carry these, so they are a layer of their own rather than a state of a parent's list.
 *
 * DECISION(2026-10-22, abhishek): an instrument whose provenance is a template match is an
 * unattached instrument like any other, drawn by this layer.
 * Template matching leaves the frontend, and the SME-verified rows it produced are neither
 * deleted nor hidden. A sheet that shows two boxes where a template match overlaps a
 * hand-marked instrument is the signal that this rule needs a merge step.
 */
export function getUnattachedInstruments(
  state: Pick<PnidWizardState, 'instruments'> | null | undefined,
): PnidInstrument[] {
  const instruments = state?.instruments ?? [];
  return instruments.filter(
    (inst) => getInstrumentEffectiveContext(inst, state).context_type === null,
  );
}

export function getNodeContextInstruments(
  state: Pick<PnidWizardState, 'instruments'> | null | undefined,
  nodeId?: string | null,
): PnidInstrument[] {
  const instruments = state?.instruments ?? [];
  return instruments
    .filter((inst) => {
      const effectiveContext = getInstrumentEffectiveContext(inst, state);
      if (effectiveContext.context_type !== 'node') return false;
      return nodeId ? effectiveContext.context_id === nodeId : true;
    })
    .sort((a, b) => {
      const aOrder = a.sequence_order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.sequence_order ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.id.localeCompare(b.id);
    });
}

export function getDirectNodeContextInstruments(
  state: Pick<PnidWizardState, 'instruments'> | null | undefined,
  nodeId?: string | null,
): PnidInstrument[] {
  const instruments = state?.instruments ?? [];
  return instruments
    .filter((inst) => {
      if (!isNodeContextInstrument(inst)) return false;
      return nodeId ? inst.context_id === nodeId : true;
    })
    .sort((a, b) => {
      const aOrder = a.sequence_order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.sequence_order ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.id.localeCompare(b.id);
    });
}
